"use client";

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { cleanupCache } from "@/lib/cache/client-ai-cache";
import { reportClientError } from "@/lib/observability/report-client-error";

/**
 * Check if a query error indicates an expired/missing auth session.
 *
 * SENTRY FIX: VIDSEEDS-2V0/2V1/29V — substring match force-redirected logged-in
 *   users on every server 5xx because hooks were fabricating "Not authenticated"
 *   error messages. The migration drift outage redirected 23+ users to /login
 *   while their sessions were perfectly valid.
 * Date: 2026-04-27 | Agent: Nightly Autofix
 * Root cause: isAuthError matched on `msg.includes("not authenticated")`, which
 *   collided with any non-auth code path that happened to use that wording.
 * Fix: Only redirect on explicit auth status codes (401/403) or PostgREST auth
 *   error codes. Drop substring matching — server-side typed responses are the
 *   only reliable signal for "this session is actually broken."
 * DO NOT: re-add substring matching on error messages — that is what caused the
 *   regression (`set_regression` on 2026-04-27 for VIDSEEDS-29V → VIDSEEDS-2V1).
 */
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as Record<string, unknown>;
  // Supabase PostgREST auth error codes (server-typed, not message-based)
  if (err.code === "PGRST301" || err.code === "42501") return true;
  // HTTP 401/403 from fetch-based queries (RpcHttpError sets `status` directly)
  if (err.status === 401 || err.status === 403) return true;
  // JWT expiry comes back as a typed Supabase error code; the message
  // alone is too fuzzy to drive a redirect.
  if (typeof err.message === "string" && err.message === "JWT expired") {
    return true;
  }
  return false;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    cleanupCache();
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            // REGRESSION GUARD — NODE-1P9 (8 regressions, escalation level 3)
            // After K8s rollout 502 windows, users were stuck with stale null data
            // because queries never refetched after network recovery.
            // DO NOT: set this back to false.
            refetchOnReconnect: true,
            retry: (failureCount, error) => {
              // Don't retry auth errors — redirect to login instead
              if (isAuthError(error)) return false;
              return failureCount < 2;
            },
          },
          mutations: {
            onError: (error) => {
              if (isAuthError(error)) {
                window.location.href = "/login?error=session_expired";
                return;
              }
              // Safety net: report any mutation error not caught by the caller
              reportClientError(error, {
                source: "react-query.mutation.global",
                feature: "unknown",
                severity: "error",
              });
            },
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            // Redirect to login on auth errors
            if (isAuthError(error)) {
              console.error(
                "[QueryProvider] Auth error detected, redirecting to login:",
                error,
              );
              window.location.href = "/login?error=session_expired";
              return;
            }
            // Report non-auth query errors
            reportClientError(error, {
              source: "react-query",
              severity: "warning",
              extra: {
                queryKey: JSON.stringify(query.queryKey).slice(0, 200),
              },
            });
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
